import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import axios from 'axios';
import SuperAdminLayout from '../../../layouts/SuperAdminLayout';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import api from './../../../utils/api';

const Cashbook = () => {
  const user = JSON.parse(localStorage.getItem('user'));
  const superAdminId = user?.id;

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [data, setData] = useState([]);

  const fetchCashbook = async () => {
    try {
      const res = await api.get(
        `/api/cashbook/${superAdminId}?from=${fromDate}&to=${toDate}`
      );
      setData(res.data);
    } catch (err) {
      console.error('Error fetching cashbook:', err);
    }
  };

  // Totals and balance
  let totalDebit = 0;
  let totalCredit = 0;
  let runningBalance = 0;

  const rowsWithBalance = data.map((entry) => {
    const debit = entry.debit || 0;
    const credit = entry.credit || 0;

    totalDebit += debit;
    totalCredit += credit;

    runningBalance = runningBalance + credit - debit;

    return {
      ...entry,
      runningBalance,
    };
  });

  const closingBalance = runningBalance;

  const downloadPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text('Cashbook Report', 14, 15);

    const tableData = data.map((row) => [
      row.date?.slice(0, 10) || '',
      row.partyName || '',
      row.remarks || '',
      row.voucherType || '',
      row.debit > 0 ? row.debit.toFixed(2) : '-',
      row.credit > 0 ? row.credit.toFixed(2) : '-',
    ]);

    // Totals
    const totalDebit = data.reduce((sum, row) => sum + (row.debit || 0), 0);
    const totalCredit = data.reduce((sum, row) => sum + (row.credit || 0), 0);

    const closingDebit =
      totalCredit > totalDebit ? totalCredit - totalDebit : 0;
    const closingCredit =
      totalDebit > totalCredit ? totalDebit - totalCredit : 0;

    // Add Closing Balance row
    tableData.push([
      '',
      '',
      '',
      'Closing Balance',
      closingDebit > 0 ? closingDebit.toFixed(2) : '-',
      closingCredit > 0 ? closingCredit.toFixed(2) : '-',
    ]);

    // Add Total row
    tableData.push([
      '',
      '',
      '',
      'Total',
      (totalDebit + closingDebit).toFixed(2),
      (totalCredit + closingCredit).toFixed(2),
    ]);

    autoTable(doc, {
      startY: 25,
      head: [
        ['Date', 'Party Name', 'Remarks', 'Voucher Type', 'Debit', 'Credit'],
      ],
      body: tableData,
      theme: 'striped',
      styles: { fontSize: 10 },
      headStyles: { fillColor: [33, 150, 243] },
    });

    doc.save('cashbook-report.pdf');
  };

  return (
    <SuperAdminLayout>
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          Cashbook (Bank Statement Style)
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <TextField
            type="date"
            label="From"
            InputLabelProps={{ shrink: true }}
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            fullWidth
          />
          <TextField
            type="date"
            label="To"
            InputLabelProps={{ shrink: true }}
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            fullWidth
          />
          <Button variant="contained" onClick={fetchCashbook}>
            Submit
          </Button>
        </Box>

        <Button variant="outlined" sx={{ mb: 2 }} onClick={downloadPDF}>
          Download PDF
        </Button>

        {rowsWithBalance.length > 0 && (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f4f4f4' }}>
                  <TableCell>Date</TableCell>
                  <TableCell>Voucher Type</TableCell>
                  <TableCell>Particulars</TableCell>
                  <TableCell>Debit (Dr)</TableCell>
                  <TableCell>Credit (Cr)</TableCell>
                  <TableCell>Balance</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {rowsWithBalance.map((entry, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{entry.date?.slice(0, 10)}</TableCell>
                    <TableCell>{entry.voucherType || '-'}</TableCell>
                    <TableCell>
                      {entry.partyName} <br />
                      <Typography variant="caption" color="text.secondary">
                        {entry.remarks || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {entry.debit ? entry.debit.toFixed(2) : '-'}
                    </TableCell>
                    <TableCell>
                      {entry.credit ? entry.credit.toFixed(2) : '-'}
                    </TableCell>
                    <TableCell>₹{entry.runningBalance.toFixed(2)}</TableCell>
                  </TableRow>
                ))}

                {/* Final Totals Row */}
                <TableRow
                  sx={{ backgroundColor: '#e0e0e0', fontWeight: 'bold' }}
                >
                  <TableCell colSpan={3}>Total</TableCell>
                  <TableCell>{totalDebit.toFixed(2)}</TableCell>
                  <TableCell>{totalCredit.toFixed(2)}</TableCell>
                  <TableCell>₹{closingBalance.toFixed(2)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>
    </SuperAdminLayout>
  );
};

export default Cashbook;
